// src/products/equityManager/pages/ActivityLogsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Filter, Download, RefreshCw, Calendar, User, Building, FileText } from 'lucide-react';
import { supabase } from '../../../config';
import { getUserDisplayName } from '../../../services/utilityService';

const ActivityLogsPage = ({ selectedCompany, companies }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('company'); // 'all', 'company', 'me'
  const [users, setUsers] = useState({});

  const loadUserNames = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, username');
      
      if (data) {
        const userMap = {};
        data.forEach(user => {
          userMap[user.id] = getUserDisplayName(user, 'Unknown');
        });
        setUsers(userMap);
      }
    } catch (error) {
      console.error('Error loading user names:', error);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('equity_logs')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(100);

      if (filter === 'company' && selectedCompany?.id) {
        // Show logs for current company
        query = query.eq('company_id', selectedCompany.id);
      } else if (filter === 'me') {
        // Show only current user's logs
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          query = query.eq('user_id', user.id);
        }
      }
      // 'all' shows all logs the user has access to (based on RLS)

      const { data, error } = await query;

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filter, selectedCompany?.id]);

  useEffect(() => {
    loadUserNames();
    loadLogs();
  }, [filter, selectedCompany?.id, loadUserNames, loadLogs]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'N/A';
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch {
      return 'N/A';
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('login') || action.includes('logout')) return <User className="w-4 h-4" />;
    if (action.includes('company')) return <Building className="w-4 h-4" />;
    if (action.includes('page')) return <FileText className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('login')) return 'text-green-600 bg-green-50';
    if (action.includes('logout')) return 'text-red-600 bg-red-50';
    if (action.includes('company')) return 'text-blue-600 bg-blue-50';
    if (action.includes('page')) return 'text-purple-600 bg-purple-50';
    return 'text-gray-600 bg-gray-50';
  };

  const exportLogs = () => {
    const csv = [
      ['Timestamp', 'User', 'Action', 'Company', 'Details'].join(','),
      ...logs.map(log => [
        formatTimestamp(log.timestamp),
        users[log.user_id] || log.user_id || 'Unknown',
        log.action || 'N/A',
        log.company_id ? companies.find(c => c.id === log.company_id)?.name || log.company_id : 'N/A',
        JSON.stringify(log.details || {}).replace(/"/g, '""')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `equity-activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Logs</h1>
          <p className="text-sm text-gray-600 mt-1">
            Audit trail for {selectedCompany ? `${selectedCompany.name}` : 'your companies'}
          </p>
        </div>
        <div className="flex gap-2">
          {logs.length > 0 && (
            <button
              onClick={exportLogs}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}
          <button
            onClick={loadLogs}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow">
        <Filter className="w-5 h-5 text-gray-500" />
        <div className="flex gap-2">
          {[
            { value: 'company', label: selectedCompany ? 'This Company' : 'Company' },
            { value: 'me', label: 'My Actions' },
            { value: 'all', label: 'All Accessible' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === option.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading activity logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No activity logs found</p>
            <p className="text-sm mt-2">
              {filter === 'company' && !selectedCompany 
                ? 'Please select a company to view its activity logs'
                : 'Activity will appear here as you use the system'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log) => (
              <div key={log.id} className="p-4 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{log.action || 'N/A'}</span>
                          {log.company_id && (
                            <span className="text-xs text-gray-500">
                              • {companies.find(c => c.id === log.company_id)?.name || `Company ${log.company_id}`}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {users[log.user_id] || log.user_id?.substring(0, 8) || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatTimestamp(log.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <details className="mt-2 ml-12 cursor-pointer">
                        <summary className="text-sm text-blue-600 hover:text-blue-800">
                          View details
                        </summary>
                        <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-auto max-w-2xl">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default ActivityLogsPage;

